/**
 * Tiny RAF-based tween. Lifted-in-spirit from cs-core's animation choreography
 * style. v0 supports the easing curves we actually use for slot reels; expand
 * as new game widgets need more.
 */

export type EasingFn = (t: number) => number;

export const Easings = {
  linear: (t: number) => t,
  easeOutCubic: (t: number) => 1 - Math.pow(1 - t, 3),
  easeOutQuart: (t: number) => 1 - Math.pow(1 - t, 4),
  easeInOutCubic: (t: number) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
  easeOutBack: (t: number) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  },
} satisfies Record<string, EasingFn>;

export interface TweenOptions {
  durationMs: number;
  easing?: EasingFn;
  /** Called on each frame with the eased progress in [0, 1]. */
  onUpdate: (progress: number) => void;
  /** Called once when {@code progress === 1}, after the final update. */
  onComplete?: () => void;
}

export interface TweenHandle {
  cancel(): void;
  isActive(): boolean;
}

export function tween(options: TweenOptions): TweenHandle {
  const easing = options.easing ?? Easings.linear;
  let active = true;
  let startMs: number | null = null;
  let rafId = 0;

  const step = (now: number) => {
    if (!active) {
      return;
    }
    if (startMs === null) {
      startMs = now;
    }
    const elapsed = now - startMs;
    const t = Math.min(1, elapsed / options.durationMs);
    options.onUpdate(easing(t));
    if (t >= 1) {
      active = false;
      options.onComplete?.();
      return;
    }
    rafId = requestAnimationFrame(step);
  };

  rafId = requestAnimationFrame(step);

  return {
    cancel(): void {
      if (active) {
        active = false;
        cancelAnimationFrame(rafId);
      }
    },
    isActive(): boolean {
      return active;
    },
  };
}

/**
 * Promise-returning wrapper around {@link tween}. Use when the caller wants
 * to {@code await} a finished animation rather than nest a callback chain.
 *
 * <p>The returned promise rejects on cancel via the optional onCancel hook
 * — pass {@code rejectOnCancel: true} to surface scene-destroy aborts as
 * thrown exceptions; default is to resolve silently so the calling code
 * doesn't have to wrap each await in try/catch when the cancel was an
 * expected lifecycle exit.
 */
export function tweenAsync(
  options: Omit<TweenOptions, 'onComplete'> & { onComplete?: () => void },
): { handle: TweenHandle; done: Promise<void> } {
  let resolve!: () => void;
  const done = new Promise<void>((r) => {
    resolve = r;
  });
  const handle = tween({
    ...options,
    onComplete: () => {
      options.onComplete?.();
      resolve();
    },
  });
  return { handle, done };
}

/**
 * Tracks active {@link TweenHandle} instances so they can all be cancelled
 * together — e.g. when a scene component is destroyed mid-animation. Use
 * {@link start} instead of {@link tween} to register the handle; call
 * {@link cancelAll} from the owner's destroy() to abort everything in flight.
 *
 * <p>Completed tweens are removed from the tracking list automatically so
 * long-lived components don't leak handle references.
 */
export class TweenGroup {
  private handles: TweenHandle[] = [];

  start(options: TweenOptions): TweenHandle {
    let handle: TweenHandle;
    handle = tween({
      ...options,
      onComplete: () => {
        this.untrack(handle);
        options.onComplete?.();
      },
    });
    this.handles.push(handle);
    return handle;
  }

  startAsync(options: Omit<TweenOptions, 'onComplete'> & { onComplete?: () => void }): Promise<void> {
    const { handle, done } = tweenAsync({
      ...options,
      onComplete: () => {
        this.untrack(handle);
        options.onComplete?.();
      },
    });
    this.handles.push(handle);
    return done;
  }

  cancelAll(): void {
    const snapshot = this.handles.slice();
    this.handles.length = 0;
    for (const h of snapshot) h.cancel();
  }

  private untrack(handle: TweenHandle): void {
    const idx = this.handles.indexOf(handle);
    if (idx >= 0) this.handles.splice(idx, 1);
  }
}
